import { z } from 'zod';
import { FractionSchema, IdSchema, InstantSchema, LocalDateSchema } from '../common/index.js';

/** Généalogie et dévolution (architecture §2.3) : personnes par audience, liens, successions, parts exactes. */

export const VitalStatusSchema = z.enum(['vivant', 'decede', 'inconnu']).meta({ id: 'VitalStatus' });
export const ReachabilitySchema = z.enum(['joignable', 'a_relancer', 'injoignable']).meta({ id: 'Reachability' });

export const PartialDateSchema = z
  .object({
    value: z
      .string()
      .regex(/^\d{4}(-\d{2}-\d{2})?$/)
      .nullable(),
    precision: z.enum(['jour', 'annee', 'inconnue']),
  })
  .meta({ id: 'PartialDate', description: 'Date de précision variable (jour, année seule, inconnue).' });

export const ContactTypeSchema = z.enum(['phone', 'whatsapp', 'email', 'address']).meta({ id: 'ContactType' });

/** Situation matrimoniale déclarée (codes neutres, libellés dans la donnée versionnée de l'écran d'appel). */
export const MaritalStatusSchema = z.enum(['celibataire', 'marie', 'pacse', 'veuf', 'divorce', 'remarie']).meta({ id: 'MaritalStatus' });

/** Une coordonnée ajoutée seule (arbitrage du 26/10) : chiffrée par la clé maîtresse comme à l'import, visibilité équipe par défaut. */
export const ContactCreateSchema = z
  .object({
    type: ContactTypeSchema,
    value: z.string().trim().min(1).max(320),
    visibility: z.enum(['equipe', 'notaire', 'famille']).default('equipe'),
  })
  .meta({ id: 'ContactCreate' });

export const ContactStaffViewSchema = z
  .object({
    id: IdSchema,
    type: ContactTypeSchema,
    value: z.string().min(1),
    visibility: z.enum(['equipe', 'notaire', 'famille']),
    maskedForFamily: z.boolean(),
    isPreferred: z.boolean(),
    timezone: z.string().nullable(),
  })
  .meta({ id: 'ContactStaffView', description: 'Coordonnée vue par l’équipe, avec le badge « masquée pour la famille ».' });

export const ContactPreferencesSchema = z
  .object({
    preferredChannel: z.enum(['whatsapp', 'email', 'telephone', 'courrier']).nullable(),
    contactOnlyByCabinet: z.boolean(),
    delegatedContactUserId: IdSchema.nullable(),
    language: z.string().regex(/^[a-z]{2,3}$/),
  })
  .meta({ id: 'ContactPreferences' });

export const SensitiveLabelRefSchema = z
  .object({ id: IdSchema, label: z.string().min(1), sharedOutsideTeam: z.boolean() })
  .meta({ id: 'SensitiveLabelRef' });

const identite = {
  id: IdSchema,
  firstNames: z.string(),
  lastName: z.string().min(1),
  birthName: z.string().nullable(),
  birthDate: PartialDateSchema,
  birthPlace: z.string().nullable(),
  deathDate: LocalDateSchema.nullable(),
  deathPlace: z.string().nullable(),
  vitalStatus: VitalStatusSchema,
  isHypothesis: z.boolean(),
  relationLabel: z.string().nullable(),
};

export const PersonRoleSchema = z.enum(['auteur', 'mandant', 'heritier', 'conjoint', 'autre']).meta({ id: 'PersonRole' });

export const PersonStaffViewSchema = z
  .object({
    ...identite,
    dossierId: IdSchema,
    sex: z.enum(['F', 'M', 'X']).nullable(),
    role: PersonRoleSchema,
    reachability: ReachabilitySchema,
    contacts: z.array(ContactStaffViewSchema),
    preferences: ContactPreferencesSchema,
    sensitiveLabels: z.array(SensitiveLabelRefSchema),
    rowVersion: z.number().int().min(0),
    /** Adresse, profession et situation déclarées (arbitrage du 26/10) ; null sans réponse. */
    address: z.string().nullable().optional(),
    profession: z.string().nullable().optional(),
    maritalStatus: MaritalStatusSchema.nullable().optional(),
  })
  .meta({ id: 'PersonStaffView', description: 'Fiche héritier complète (équipe MAAT).' });

export const PersonClercViewSchema = z
  .strictObject({
    ...identite,
    identityVerified: z.boolean(),
    contactsOnRequest: z.boolean(),
  })
  .meta({ id: 'PersonClercView', description: 'Personne vue par la clerc ou le notaire : état civil, coordonnées sur demande.' });

export const PersonFamilyViewSchema = z
  .strictObject({
    id: IdSchema,
    displayName: z.string().min(1),
    relationLabel: z.string().nullable(),
    vitalStatus: VitalStatusSchema,
    isHypothesis: z.boolean(),
    sharedContacts: z.array(z.strictObject({ type: ContactTypeSchema, value: z.string().min(1) })),
    /** ARB-GENEA-47 : la ligne de la personne qui consulte (« (vous) »). */
    isViewer: z.boolean(),
    /** ARB-GENEA-46 : « accepte » seulement si la personne a accepté d'être contactée ; sinon null (un refus n'est jamais dit). */
    contactStatus: z.enum(['accepte']).nullable(),
  })
  .meta({ id: 'PersonFamilyView', description: 'Personne vue par la famille : coordonnées seulement si partagées.' });

export const PersonSummarySchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    displayName: z.string().min(1),
    vitalStatus: VitalStatusSchema,
    relationLabel: z.string().nullable(),
  })
  .meta({ id: 'PersonSummary' });

export const PersonWriteSchema = z
  .object({
    firstNames: z.string(),
    lastName: z.string().min(1),
    birthName: z.string().nullable().optional(),
    sex: z.enum(['F', 'M', 'X']).nullable().optional(),
    birthDate: PartialDateSchema.optional(),
    birthPlace: z.string().nullable().optional(),
    deathDate: LocalDateSchema.nullable().optional(),
    deathPlace: z.string().nullable().optional(),
    vitalStatus: VitalStatusSchema,
    isHypothesis: z.boolean().default(false),
    role: PersonRoleSchema.default('heritier'),
  })
  .meta({ id: 'PersonWrite' });

export const PersonPatchSchema = PersonWriteSchema.partial()
  .extend({
    /** Arbitrage du 26/10 : écrits par l'appel (M02) ou la fiche ; null efface. */
    address: z.string().trim().min(1).max(500).nullable().optional(),
    profession: z.string().trim().min(1).max(200).nullable().optional(),
    maritalStatus: MaritalStatusSchema.nullable().optional(),
  })
  .meta({ id: 'PersonPatch' });

export const RelationshipSchema = z
  .object({
    id: IdSchema,
    kind: z.enum(['filiation', 'union']),
    personAId: IdSchema,
    personBId: IdSchema,
    unionType: z.enum(['mariage', 'pacs', 'concubinage']).nullable(),
    filiationType: z.enum(['biologique', 'adoptive_pleniere', 'adoptive_simple']).nullable(),
    startDate: LocalDateSchema.nullable(),
    endDate: LocalDateSchema.nullable(),
  })
  .meta({ id: 'Relationship', description: 'Lien de filiation (A parent de B) ou d’union.' });

export const RelationshipCreateSchema = RelationshipSchema.omit({ id: true }).meta({ id: 'RelationshipCreate' });

/**
 * Fratrie (décision du 30/10, ADR 0064) : la fratrie n'est pas un lien à part, elle se déduit des parents communs, comme le moteur
 * de dévolution la lit (C. civ. 734, 737, 738, 752). Ajouter un frère ou une sœur de `siblingOfPersonId` reprend ses parents :
 * tous pour un germain, `commonParentId` seul pour un demi. Un parent commun non saisi est créé « à rechercher », au statut vital
 * inconnu, dans la même transaction.
 */
export const SiblingCreateSchema = z
  .object({
    person: PersonWriteSchema,
    siblingOfPersonId: IdSchema,
    siblingKind: z.enum(['germain', 'demi']),
    /** Demi seulement : le parent commun, parmi ceux de `siblingOfPersonId` (requis quand elle en a deux). */
    commonParentId: IdSchema.nullable().default(null),
  })
  .meta({ id: 'SiblingCreate', description: 'Ajout d’un frère ou d’une sœur (germain ou demi) par ses parents communs.' });

export const SiblingCreatedSchema = z
  .object({
    person: PersonStaffViewSchema,
    /** Parents communs créés « à rechercher » (statut vital inconnu) faute d'être saisis. */
    createdParents: z.array(PersonStaffViewSchema),
    relationships: z.array(RelationshipSchema),
  })
  .meta({ id: 'SiblingCreated', description: 'Le frère ou la sœur ajouté, les parents créés et les liens de filiation écrits.' });

export const SuccessionSchema = z
  .object({
    id: IdSchema,
    deceasedPersonId: IdSchema,
    openedAt: LocalDateSchema,
    deathInDomicileDepartment: z.boolean(),
    hasDispositions: z.boolean(),
    status: z.enum(['ouverte', 'notoriete_faite', 'close']),
  })
  .meta({ id: 'Succession', description: 'Succession d’un défunt (un dossier en contient souvent plusieurs).' });

export const HeirOptionSchema = z
  .object({
    successionId: IdSchema,
    personId: IdSchema,
    option: z.enum(['silence', 'acceptation', 'acceptation_actif_net', 'renonciation']),
    datedAt: LocalDateSchema.nullable(),
    protection: z.enum(['aucune', 'mineur', 'tutelle', 'curatelle']),
  })
  .meta({ id: 'HeirOption' });

export const ModeDevolutionSchema = z.enum(['propre', 'representation', 'transmission']).meta({
  id: 'ModeDevolution',
  description:
    'propre : part reçue directement ; representation : à la place d’un prédécédé (C. civ. 751) ; transmission : par la succession d’un héritier décédé après le défunt.',
});

export const StatutDevolutionSchema = z.enum(['a_valider_notaire', 'valide_notaire', 'remplace']).meta({ id: 'StatutDevolution' });

export const ShareSchema = z
  .object({
    personId: IdSchema,
    successionId: IdSchema,
    share: FractionSchema,
    mode: ModeDevolutionSchema,
    viaPersonId: IdSchema.nullable(),
  })
  .meta({ id: 'Share', description: 'Part d’une personne dans une succession, avec son mode d’acquisition.' });

export const ReserveSchema = z
  .object({ successionId: IdSchema, branchLabel: z.string().min(1), share: FractionSchema, reason: z.string().min(1) })
  .meta({ id: 'Reserve', description: 'Part mise en réserve pour une branche inconnue ou non identifiée.' });

export const SpouseOptionSchema = z
  .object({
    spousePersonId: IdSchema,
    successionId: IdSchema,
    status: z.enum(['non_exercee', 'usufruit_total', 'quart_pleine_propriete']),
    allChildrenCommon: z.boolean(),
    scenarios: z.array(z.object({ option: z.enum(['usufruit_total', 'quart_pleine_propriete']), shares: z.array(ShareSchema) })),
  })
  .meta({ id: 'SpouseOption', description: 'Option du conjoint survivant (C. civ. 757, 758-3) et ses deux scénarios.' });

export const DevolutionResultSchema = z
  .object({
    successionId: IdSchema,
    shares: z.array(ShareSchema),
    reserves: z.array(ReserveSchema),
    explanation: z.array(z.string().min(1)),
    status: StatutDevolutionSchema,
    engineVersion: z.string().min(1),
    computedAt: InstantSchema,
    spouseOption: SpouseOptionSchema.nullable(),
  })
  .meta({ id: 'DevolutionResult', description: 'Résultat reproductible de la dévolution, toujours « à valider notaire » d’abord.' });

export const TreeNodeSchema = z
  .object({
    id: IdSchema,
    displayName: z.string().min(1),
    vitalStatus: VitalStatusSchema,
    isHypothesis: z.boolean(),
    generation: z.number().int(),
    role: PersonRoleSchema,
    reachability: ReachabilitySchema.nullable(),
    maskedForFamily: z.boolean(),
    /** RFC-GENEA28-001 : coordonnées de la personne pour l'équipe (FamilyTree n'existe que dans l'audience staff). */
    contacts: z.array(
      z.strictObject({ type: ContactTypeSchema, value: z.string().min(1), visibility: z.enum(['equipe', 'notaire', 'famille']) }),
    ),
  })
  .meta({ id: 'TreeNode' });

export const FamilyTreeSchema = z
  .object({
    dossierId: IdSchema,
    nodes: z.array(TreeNodeSchema),
    relationships: z.array(RelationshipSchema),
    successions: z.array(SuccessionSchema),
    devolutions: z.array(DevolutionResultSchema),
    completeness: z.number().int().min(0).max(100),
  })
  .meta({ id: 'FamilyTree', description: 'Arbre complet et parts calculées (équipe, clerc).' });
